/**
 * The between-acts intermission.
 *
 * An act ends, the map changes, and the run stops until the player has made one
 * decision. That pause is the point: a hundred rounds of uninterrupted prep
 * timers gives the player nowhere to look up from the board, and `Levels.ts`'s
 * whole argument is that the structural changes — new corridor, wider slot
 * budget, another tower on the shelf — are what stops the late game flattening.
 * A change nobody is made to notice may as well not have happened.
 *
 * Three blocks, in the order the player needs them:
 *
 * 1. **What changed.** The next act's map, its slot budget and its centreline,
 *    against the act just finished. These are the numbers `Balance.ts` uses, so
 *    the panel is showing the player the actual model input rather than flavour.
 * 2. **What unlocked.** Towers the act puts on the shelf, if any.
 * 3. **The choice.** Two abilities, one active and one passive. Picking is
 *    mandatory; there is no third button that means "neither".
 *
 * The board refund is stated here rather than discovered: towers do not survive
 * a map change (`Session.beginAct`), and a player who reads that *after* the map
 * has already gone has been robbed rather than informed.
 *
 * ## Why this file carries its own shell and its own stylesheet
 *
 * It used to borrow `PauseMenu.ModalShell` and keep its rules in `ui.css`. Both
 * of those files belong to the responsive-layout pass, and a full-bleed modal is
 * exactly the kind of surface that breaks when someone retunes the shared modal
 * box underneath it. So the shell, the focus trap and every rule below live
 * here, under a `pxa-` prefix that collides with nothing, and every custom
 * property is read with a literal fallback so the panel still draws if
 * `installUiTheme` has not run.
 *
 * The `.act__*` block left behind in `ui.css` is dead and can be deleted.
 *
 * ## Viewport
 *
 * Mobile-first, and verified rather than assumed. The awkward case is not the
 * phone, it is **landscape at 844x390**: two ability cards, four stats and three
 * paragraphs do not fit in 390px of height at any type size, so the surface
 * scrolls and the short-viewport query flattens the stats to four columns and
 * drops the card blurbs. Checked at 390x844, 650x744, 844x390 and 1920x1080.
 */

import { el, icon, iconFromId, installUiTheme, money } from './Icon';
import type { IconName } from './Icon';

/** The minimum an act must expose to be shown. Satisfied by `Levels.Act`. */
export interface ActSummary {
  readonly index: number;
  readonly name: string;
  readonly blurb: string;
  readonly first: number;
  readonly last: number;
  readonly map: string;
  readonly slots: number;
  readonly trackLength: number;
}

/** The minimum an ability must expose. Satisfied by `Levels.Ability`. */
export interface AbilityOffer {
  readonly id: string;
  readonly name: string;
  readonly icon: string;
  readonly blurb: string;
  readonly kind: 'active' | 'passive';
  readonly cooldown: number;
  readonly duration: number;
}

/**
 * A tower the act puts on the shelf.
 *
 * It used to be a display name in a comma list, which is how a run's only
 * reward came to read as a grey footnote. The mark and the price are what a
 * player recognises a tower by — they are the two things the shelf itself
 * shows — so they come along.
 */
export interface UnlockedTower {
  readonly name: string;
  /** Icon id from the vendored set, resolved through `iconFromId`. */
  readonly icon: string;
  readonly cost: number;
}

export interface ActPanelState {
  open: boolean;
  finished: ActSummary;
  next: ActSummary;
  offers: readonly AbilityOffer[];
  /** The towers the next act unlocks. May be empty. */
  unlocks: readonly UnlockedTower[];
  /** Cash returned by the board refund, for the line that explains it. */
  refunded: number;
}

export interface ActPanelCallbacks {
  /** The player took an ability. The run resumes on the next act. */
  onChoose?(id: string): void;
}

const FALLBACK_ICON: IconName = 'shapes';

const FOCUSABLE = 'button:not(:disabled), [tabindex]:not([tabindex="-1"])';

// ───────────────────────────────────────────────────────────── stylesheet ──

/**
 * Injected once, on first mount. Mobile-first: the base rules are the 390px
 * phone and the two queries widen rather than narrow.
 *
 * Every `var()` carries a literal fallback. `Icon.installUiTheme` publishes the
 * `--ui-*` properties and this panel calls it, but a panel that renders black on
 * black because a theme call moved is a worse failure than a slightly-off hue.
 */
const CSS = `
.pxa-scrim {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: max(8px, env(safe-area-inset-top)) max(8px, env(safe-area-inset-right))
           max(8px, env(safe-area-inset-bottom)) max(8px, env(safe-area-inset-left));
  background: color-mix(in srgb, #05070a 82%, transparent);
  overscroll-behavior: contain;
}
.pxa-scrim[hidden] { display: none; }

.pxa-box {
  --pxa-line: color-mix(in srgb, var(--ui-accent, #4cf3ff) 42%, transparent);
  box-sizing: border-box;
  width: 100%;
  max-width: 560px;
  max-height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 14px;
  color: var(--ui-text, #9aa5b1);
  background: var(--ph-black, #05070a);
  border: 1px solid var(--ui-accent, #4cf3ff);
  border-radius: 2px;
}

/*
 * The eyebrow, and the title it is not.
 *
 * 'ACT I COMPLETE' used to be the dominant line on the screen, which put the
 * largest type on the only thing here the player can do nothing about. The act
 * that just ended is a receipt; the act about to begin is the subject of every
 * other word on the panel. So the receipt becomes an 11px eyebrow and the
 * subject takes the heading.
 */
.pxa-eyebrow {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 2px;
  font-size: 11px;
  line-height: 1.4;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ui-disabled, #4b5460);
}

.pxa-title {
  margin: 0 0 6px;
  font-size: 18px;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ui-text-bright, #f4f9ff);
  overflow-wrap: anywhere;
}
.pxa-title .pxa-act-no { color: var(--ui-accent, #4cf3ff); }

.pxa-lead { margin: 0 0 12px; font-size: 11px; line-height: 1.6; }

/*
 * The refund, given the weight of the thing it describes.
 *
 * This was one yellow paragraph between a padlock and the offers, and it is the
 * single most consequential sentence on the screen: the player's entire defence
 * has just been deleted and returned as money, and the act opens on an empty
 * board. A run that reads past it walks into the next round with three towers
 * where it wants twelve. It gets a box, a rule down its leading edge in cash
 * yellow, and the number set large enough to be the thing the eye lands on.
 */
.pxa-refund {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 2px 10px;
  margin: 0 0 12px;
  padding: 10px 12px;
  background: color-mix(in srgb, var(--ui-cash, #ffdc3d) 8%, transparent);
  border: 1px solid color-mix(in srgb, var(--ui-cash, #ffdc3d) 40%, transparent);
  border-left-width: 3px;
  border-radius: 2px;
}
.pxa-refund > .pxi { grid-area: 1 / 1 / 3 / 2; margin-top: 2px; }
.pxa-refund-head {
  grid-area: 1 / 2 / 2 / 3;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
  font-size: 11px;
  line-height: 1.4;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ui-cash, #ffdc3d);
}
.pxa-refund-sum {
  font-size: 19px;
  line-height: 1.25;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--ui-cash, #ffdc3d);
}
.pxa-refund-body {
  grid-area: 2 / 2 / 3 / 3;
  margin: 0;
  font-size: 11px;
  line-height: 1.5;
}

/*
 * The four numbers as a strip of chips rather than a definition list.
 *
 * 'Slots 14 was 10' is four more buildable positions and the widest board the
 * act allows; 'Track 1345px was 853px' is half a corridor again of walking time
 * to shoot into. Set as label-over-value with the *change* beside the value and
 * pointing, they read as what they are — gains — instead of as a table with a
 * grey suffix.
 */
.pxa-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 0 0 12px;
  padding: 0;
  font-size: 11px;
}
.pxa-stat { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.pxa-stat dt {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ui-disabled, #4b5460);
}
.pxa-stat dd {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 6px;
  margin: 0;
  min-width: 0;
  font-weight: 700;
  color: var(--ui-text-bright, #f4f9ff);
  overflow-wrap: anywhere;
}
/* The direction, in its own colour, so a gain reads before it is read. */
.pxa-delta {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--ui-wave, #71ff3c);
}
.pxa-was { font-size: 10px; font-weight: 400; color: var(--ui-disabled, #4b5460); }

/* A section heading: the two blocks below each need one, and they are the same
   heading drawn twice. */
.pxa-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 6px;
  margin: 0 0 6px;
  font-size: 11px;
  line-height: 1.4;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ui-accent, #4cf3ff);
}
/* The qualifier gets its own line on a phone, where a 374px heading has no
   room for one beside it, and rides the trailing edge once there is. */
.pxa-head-note {
  flex: 1 0 100%;
  letter-spacing: 0.06em;
  text-transform: none;
  color: var(--ui-disabled, #4b5460);
}

/*
 * The towers the act hands over, with the marks the shelf shows them under.
 *
 * Two of these arrive per act at most, and they arrived as one line of grey
 * text behind a padlock — a closed padlock, on a line whose whole meaning is
 * that something opened. The mark is 'lock-open' now and the towers are cards
 * with their own artwork and their own price, which is how a player will
 * recognise them when they go looking on the shelf a second later.
 */
.pxa-unlocks { display: flex; flex-wrap: wrap; gap: 8px; margin: 0 0 12px; }
.pxa-unlocks[hidden] { display: none; }
.pxa-unlock {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 160px;
  min-width: 0;
  padding: 7px 10px;
  background: color-mix(in srgb, var(--ui-accent, #4cf3ff) 6%, transparent);
  border: 1px solid var(--pxa-line);
  border-radius: 2px;
}
.pxa-unlock-name {
  min-width: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ui-text-bright, #f4f9ff);
  overflow-wrap: anywhere;
}
.pxa-unlock-cost {
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  color: var(--ui-cash, #ffdc3d);
}

/* One column on a phone. Two only where two can read as a pair. */
.pxa-offers { display: grid; grid-template-columns: 1fr; gap: 8px; }

.pxa-offer {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  min-width: 0;
  text-align: left;
  font: inherit;
  color: inherit;
  background: color-mix(in srgb, var(--ui-accent, #4cf3ff) 6%, transparent);
  border: 1px solid var(--pxa-line);
  border-radius: 2px;
  cursor: pointer;
  transition: border-color 90ms steps(2), background 90ms steps(2);
}
.pxa-offer:hover,
.pxa-offer:focus-visible {
  border-color: var(--ui-accent, #4cf3ff);
  background: color-mix(in srgb, var(--ui-accent, #4cf3ff) 16%, transparent);
}
.pxa-offer:active { transform: translateY(1px); }

.pxa-offer-head { display: flex; align-items: center; gap: 8px; min-width: 0; }
.pxa-offer-name {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ui-text-bright, #f4f9ff);
  overflow-wrap: anywhere;
}
.pxa-offer-meta { font-size: 10px; letter-spacing: 0.06em; color: var(--ui-accent, #4cf3ff); }
.pxa-offer-blurb { font-size: 11px; line-height: 1.5; }

/*
 * And the line that makes a card read as something you press.
 *
 * The two cards were the only input the screen requires and they carried the
 * visual weight of a stat row: no verb, no affordance, nothing to say one of
 * them has to be taken before the run goes on. The rule and the verb cost eight
 * pixels and answer both questions at once.
 */
.pxa-offer-take {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  padding-top: 6px;
  border-top: 1px solid var(--pxa-line);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ui-accent, #4cf3ff);
}

/* 560px is where two cards stop being cramped, not where the screen stops being small. */
@media (min-width: 560px) {
  .pxa-box { padding: 16px; }
  .pxa-title { font-size: 20px; }
  .pxa-stats { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .pxa-head-note { flex: 0 1 auto; margin-left: auto; text-align: right; }
  .pxa-offers { grid-template-columns: 1fr 1fr; }
  .pxa-offer { padding: 12px; }
}

/*
 * Landscape phones and short desktop windows. 844x390 leaves 374px of usable
 * height for a title, four stats, three paragraphs and two cards — the surface
 * has to scroll, and everything that can be flattened is.
 */
@media (max-height: 480px) {
  .pxa-scrim { place-items: start center; }
  .pxa-box { max-width: 720px; padding: 10px 12px; }
  .pxa-title { font-size: 15px; margin-bottom: 4px; }
  .pxa-lead { margin-bottom: 8px; }
  .pxa-refund { margin-bottom: 8px; padding: 8px 10px; }
  .pxa-refund-sum { font-size: 16px; }
  .pxa-stats { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; margin-bottom: 8px; }
  .pxa-unlocks { margin-bottom: 8px; }
  .pxa-unlock { padding: 5px 8px; }
  .pxa-offers { grid-template-columns: 1fr 1fr; gap: 8px; }
  .pxa-offer { padding: 8px 10px; gap: 4px; }
  /*
   * Whole, not clamped. Dropping the blurb saved forty pixels and cost the
   * player the only sentence that says what BARRAGE *does* — a choice between
   * two names is not a choice — so it was clamped to two lines instead. Two
   * lines is not what these sentences are: 'Every tower in the ring fires at
   * eighty percent over rated for six seconds. Barrels are consumable.' wants
   * three at 321px, and the third one held the cost. This surface already
   * scrolls at 844x390 and says so at the top of the file; fifteen pixels of
   * scroll is a cheaper price than the clause that ends 'consumable'.
   */
  .pxa-offer-blurb { line-height: 1.4; }
  .pxa-head-note { flex: 0 1 auto; margin-left: auto; text-align: right; }
}

@media (prefers-reduced-motion: reduce) {
  .pxa-offer { transition-duration: 1ms; }
}
`;

let styleInstalled = false;

function installStyles(): void {
  if (styleInstalled || typeof document === 'undefined') return;
  styleInstalled = true;
  const tag = document.createElement('style');
  tag.id = 'pxa-act-panel';
  tag.textContent = CSS;
  document.head.append(tag);
}

// ───────────────────────────────────────────────────────────────── panel ──

export class ActPanel {
  private scrim = el('div', 'pxa-scrim');
  private box = el('div', 'pxa-box');
  private eyebrow = el('p', 'pxa-eyebrow');
  private titleRow = el('h2', 'pxa-title');
  private lead = el('p', 'pxa-lead');
  private refundRow = el('div', 'pxa-refund');
  private stats = el('dl', 'pxa-stats');
  private unlockHead = el('p', 'pxa-head');
  private unlockRow = el('div', 'pxa-unlocks');
  private offerHead = el('p', 'pxa-head');
  private offerRow = el('div', 'pxa-offers');
  private returnFocus: HTMLElement | null = null;
  private mounted = false;
  private openNow = false;

  constructor(private readonly cb: ActPanelCallbacks = {}) {}

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    installUiTheme();
    installStyles();

    this.titleRow.id = 'pxa-act-title';
    this.box.setAttribute('role', 'dialog');
    this.box.setAttribute('aria-modal', 'true');
    this.box.setAttribute('aria-labelledby', this.titleRow.id);
    /*
     * The order is what the player needs, in the order they need it.
     *
     * Where they are (eyebrow, title, blurb), what just happened to their board
     * and what it costs them (refund), what the new act gives them (deltas, new
     * towers), and last the one thing the screen will not go on without (the
     * choice). The old order opened on the act that had ended and closed on the
     * decision, which is both halves of that backwards.
     */
    this.box.append(
      this.eyebrow, this.titleRow, this.lead,
      this.refundRow, this.stats,
      this.unlockHead, this.unlockRow,
      this.offerHead, this.offerRow,
    );
    this.scrim.append(this.box);
    this.scrim.hidden = true;
    // No scrim-click dismiss and no Escape. The pick is the only way out, and a
    // modal that closes while still owing the player an ability drops it.
    this.scrim.addEventListener('keydown', this.onKey);
    root.append(this.scrim);
  }

  update(state: ActPanelState): void {
    if (!this.mounted) return;
    if (state.open && !this.openNow) {
      this.render(state);
      const active = document.activeElement;
      this.returnFocus = active instanceof HTMLElement ? active : null;
      this.scrim.hidden = false;
      /*
       * Focus goes to the first card, and the surface does not move to meet it.
       *
       * The only focusable things in here are the two offers, and they are last
       * — so on any viewport where the panel scrolls, a plain `focus()` scrolled
       * them into view and the panel opened 32px down, with the eyebrow, the
       * title and the whole refund block already above the fold. Measured at
       * 844x390. `preventScroll` keeps the keyboard entry point and the reset
       * puts the document where a reader starts, which is the top.
       */
      this.box.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true });
      this.box.scrollTop = 0;
    } else if (!state.open && this.openNow) {
      this.scrim.hidden = true;
      this.returnFocus?.focus();
      this.returnFocus = null;
    }
    this.openNow = state.open;
  }

  get open(): boolean {
    return this.openNow;
  }

  destroy(): void {
    this.scrim.removeEventListener('keydown', this.onKey);
    this.scrim.remove();
    this.mounted = false;
    this.openNow = false;
  }

  private render(state: ActPanelState): void {
    const { finished, next } = state;

    this.eyebrow.textContent = '';
    this.eyebrow.append(
      icon('flag-checkered', { size: 12, role: 'uiAccent' }),
      document.createTextNode(`Act ${roman(finished.index)} complete`),
    );

    // The act number carries the accent and the name carries the weight, so the
    // heading reads as one line and still says which of eight this is.
    this.titleRow.textContent = '';
    this.titleRow.append(
      el('span', 'pxa-act-no', `Act ${roman(next.index)}`),
      document.createTextNode(` — ${next.name}`),
    );
    this.lead.textContent = next.blurb;

    /*
     * The refund, and the sentence that says what to do about it.
     *
     * `Session.beginAct` has already sold the board at full price by the time
     * this opens, so the number is not a promise — it is a statement of what the
     * player is holding and of what they no longer have on the field. The last
     * clause names the round the empty board has to survive, because that is the
     * part a player pays for if they read past this.
     */
    this.refundRow.textContent = '';
    const head = el('div', 'pxa-refund-head');
    head.append(
      el('span', undefined, 'Your board came back as cash'),
      el('span', 'pxa-refund-sum', money(state.refunded)),
    );
    this.refundRow.append(
      icon('coins', { size: 16, role: 'uiCash', glow: 0.8 }),
      head,
      el(
        'p',
        'pxa-refund-body',
        `The corridor moved, so every tower was sold in full. Spend it before round ${next.first} — act ${roman(next.index)} opens on an empty board and the line is only what you rebuild.`,
      ),
    );

    this.stats.textContent = '';
    this.stats.append(
      this.stat('grid', 'Map', next.map, finished.map, null),
      this.stat('expand', 'Slots', String(next.slots), null, next.slots - finished.slots),
      this.stat(
        'arrow-right', 'Track', `${next.trackLength}px`, null,
        next.trackLength - finished.trackLength, 'px',
      ),
      this.stat('flag-checkered', 'Rounds', `${next.first}–${next.last}`, null, null),
    );

    const hasUnlocks = state.unlocks.length > 0;
    this.unlockHead.hidden = !hasUnlocks;
    this.unlockRow.hidden = !hasUnlocks;
    if (hasUnlocks) {
      this.unlockHead.textContent = '';
      this.unlockHead.append(
        // `lock-open` rather than `unlock`. Both are in the vendored set and
        // both mean the same thing to the person who drew them; at 12px the
        // one with its shackle merely tilted reads as a shut padlock over a
        // line whose entire content is that something opened.
        icon('lock-open', { size: 12, role: 'uiAccent' }),
        document.createTextNode('New on the shelf'),
        el('span', 'pxa-head-note', 'Buildable from now on'),
      );
      this.unlockRow.textContent = '';
      for (const t of state.unlocks) this.unlockRow.append(this.unlock(t));
    }

    /*
     * And the heading that makes the choice a choice.
     *
     * Two mutually exclusive cards, permanent for the act, mandatory before the
     * run goes on — none of which the screen said. It says all three now, in
     * seven words, above the only input it requires.
     */
    this.offerHead.textContent = '';
    this.offerHead.append(
      icon('star', { size: 12, role: 'uiAccent', glow: 0.8 }),
      document.createTextNode('Choose one ability'),
      el('span', 'pxa-head-note', `Kept for all of act ${roman(next.index)}. The other is gone.`),
    );

    this.offerRow.textContent = '';
    for (const offer of state.offers) this.offerRow.append(this.card(offer));
  }

  /**
   * One number, and what it did.
   *
   * `was` and `delta` are alternatives, not a pair: a map changes to something
   * with a name and a slot budget changes by four, and a `+4` beside a map name
   * would be arithmetic on a word. Whichever is given is the one that prints.
   */
  private stat(
    name: IconName, label: string, value: string,
    was: string | null, delta: number | null, unit = '',
  ): HTMLElement {
    const row = el('div', 'pxa-stat');
    const dt = el('dt');
    dt.append(icon(name, { size: 12 }), document.createTextNode(label));
    const dd = el('dd', '', value);
    if (was !== null && was !== value) dd.append(el('span', 'pxa-was', `was ${was}`));
    if (delta !== null && delta !== 0) {
      const tag = el('span', 'pxa-delta');
      tag.append(
        icon(delta > 0 ? 'arrow-up' : 'arrow-down', { size: 12, role: 'uiWave' }),
        document.createTextNode(`${delta > 0 ? '+' : '−'}${Math.abs(delta)}${unit}`),
      );
      // A shrinking corridor is not a gain and must not be printed in the gain
      // colour. Nothing in `Levels.ts` does this today; the panel should not be
      // the thing that lies if something ever does.
      if (delta < 0) tag.style.color = 'var(--ui-danger, #ff4a4f)';
      dd.append(tag);
    }
    row.append(dt, dd);
    return row;
  }

  private unlock(tower: UnlockedTower): HTMLElement {
    const card = el('div', 'pxa-unlock');
    card.append(
      iconFromId(tower.icon, FALLBACK_ICON, { size: 24, role: 'uiAccent', glow: 0.8 }),
      el('span', 'pxa-unlock-name', tower.name),
      el('span', 'pxa-unlock-cost', money(tower.cost)),
    );
    return card;
  }

  private card(offer: AbilityOffer): HTMLElement {
    const btn = el('button', 'pxa-offer');
    btn.type = 'button';

    const head = el('div', 'pxa-offer-head');
    head.append(
      iconFromId(offer.icon, FALLBACK_ICON, { size: 16, role: 'uiAccent', glow: 0.8 }),
      el('span', 'pxa-offer-name', offer.name),
    );

    const meta = el('span', 'pxa-offer-meta');
    meta.textContent = offer.kind === 'passive'
      ? 'Passive — always on'
      : `Active — ${fmt(offer.cooldown)}s cooldown${offer.duration > 0 ? `, ${fmt(offer.duration)}s` : ''}`;

    const take = el('span', 'pxa-offer-take');
    take.append(
      icon('arrow-right', { size: 12, role: 'uiAccent' }),
      document.createTextNode('Take it'),
    );

    btn.append(head, meta, el('span', 'pxa-offer-blurb', offer.blurb), take);
    btn.setAttribute('aria-label', `Take ${offer.name}. ${offer.blurb}`);
    btn.addEventListener('click', () => this.cb.onChoose?.(offer.id));
    return btn;
  }

  /** Traps Tab inside the box and swallows Escape. */
  private readonly onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      ev.stopPropagation();
      ev.preventDefault();
      return;
    }
    if (ev.key !== 'Tab') return;
    const items = [...this.box.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (ev.shiftKey && active === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && active === last) {
      ev.preventDefault();
      first.focus();
    }
  };
}

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(1));

const NUMERALS: readonly string[] = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];

/** Acts are numbered in the fiction, not in the data. */
export function roman(n: number): string {
  return NUMERALS[n] ?? String(n);
}
